import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SafetyPlanData {
  id?: string;
  title: string;
  warningSigns: string;
  copingStrategies: string;
  contacts: string;
  professionals: string;
  safeEnvironment: string;
  is_public?: boolean;
  share_id?: string;
  createdAt?: string;
}

export const useSafetyPlan = () => {
  const [plan, setPlan] = useState<SafetyPlanData>({
    title: "My Safety Plan",
    warningSigns: "",
    copingStrategies: "",
    contacts: "",
    professionals: "",
    safeEnvironment: "",
    is_public: false,
  });
  const [allPlans, setAllPlans] = useState<SafetyPlanData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState<string>("");
  const { toast } = useToast();

  const loadSafetyPlans = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setIsLoading(false);
        return;
      }

      console.log("Loading safety plans for user:", session.user.id);

      const { data, error } = await supabase
        .from('SafetyPlan')
        .select('*')
        .eq('userId', session.user.id)
        .order('createdAt', { ascending: false });

      if (error) throw error;

      console.log("Loaded safety plans:", data);

      if (data && data.length > 0) {
        setAllPlans(data);
        
        // Set the most recent plan as the active plan
        const mostRecentPlan = data[0];
        setPlan({
          id: mostRecentPlan.id,
          title: mostRecentPlan.title,
          warningSigns: mostRecentPlan.warningSigns || "",
          copingStrategies: mostRecentPlan.copingStrategies || "",
          contacts: mostRecentPlan.contacts || "",
          professionals: mostRecentPlan.professionals || "",
          safeEnvironment: mostRecentPlan.safeEnvironment || "",
          is_public: mostRecentPlan.is_public || false,
          share_id: mostRecentPlan.share_id,
        });
        
        if (mostRecentPlan.is_public && mostRecentPlan.share_id) {
          setShareUrl(`${window.location.origin}/safety-plan/${mostRecentPlan.share_id}`);
        }
      }
    } catch (error: any) {
      console.error('Error loading safety plans:', error);
      toast({
        title: "Error loading safety plans",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewPlan = () => {
    const emptyPlan: SafetyPlanData = {
      title: "New Safety Plan",
      warningSigns: "",
      copingStrategies: "",
      contacts: "",
      professionals: "",
      safeEnvironment: "",
      is_public: false,
    };
    setPlan(emptyPlan);
  };

  const loadPlanById = (id: string) => {
    console.log("Loading plan by ID:", id);
    const selectedPlan = allPlans.find(p => p.id === id);
    console.log("Selected plan:", selectedPlan);
    
    if (selectedPlan) {
      setPlan({
        id: selectedPlan.id,
        title: selectedPlan.title,
        warningSigns: selectedPlan.warningSigns || "",
        copingStrategies: selectedPlan.copingStrategies || "",
        contacts: selectedPlan.contacts || "",
        professionals: selectedPlan.professionals || "",
        safeEnvironment: selectedPlan.safeEnvironment || "",
        is_public: selectedPlan.is_public || false,
        share_id: selectedPlan.share_id,
      });
      if (selectedPlan.is_public && selectedPlan.share_id) {
        setShareUrl(`${window.location.origin}/safety-plan/${selectedPlan.share_id}`);
      } else {
        setShareUrl("");
      }
    }
  };

  const saveSafetyPlan = async (newPlan: SafetyPlanData) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to save your safety plan",
          variant: "destructive",
        });
        return;
      }

      const planData = {
        ...newPlan,
        userId: session.user.id,
        updatedAt: new Date().toISOString(),
      };

      let response;
      
      if (newPlan.id) {
        // Update existing plan
        response = await supabase
          .from('SafetyPlan')
          .update(planData)
          .eq('id', newPlan.id)
          .select()
          .single();
      } else {
        // Insert new plan
        response = await supabase
          .from('SafetyPlan')
          .insert({ ...planData })
          .select()
          .single();
      }

      if (response.error) throw response.error;

      // Reload all plans to get the updated list
      await loadSafetyPlans();
      
      toast({
        title: "Safety plan saved",
        description: "Your safety plan has been saved successfully",
      });
    } catch (error: any) {
      console.error('Error saving safety plan:', error);
      toast({
        title: "Error saving safety plan",
        description: "Please try again later",
        variant: "destructive",
      });
      throw error;
    }
  };

  const toggleSharing = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to share your safety plan",
          variant: "destructive",
        });
        return;
      }

      const newIsPublic = !plan.is_public;
      const { data, error } = await supabase
        .from('SafetyPlan')
        .update({ is_public: newIsPublic })
        .eq('userId', session.user.id)
        .eq('id', plan.id)
        .select()
        .single();

      if (error) throw error;

      setPlan(prev => ({ ...prev, is_public: newIsPublic }));
      if (newIsPublic && data.share_id) {
        const url = `${window.location.origin}/safety-plan/${data.share_id}`;
        setShareUrl(url);
        toast({
          title: "Safety plan shared",
          description: "Your safety plan is now shareable via a link",
        });
      } else {
        setShareUrl("");
        toast({
          title: "Safety plan unshared",
          description: "Your safety plan is now private",
        });
      }
    } catch (error: any) {
      console.error('Error toggling safety plan sharing:', error);
      toast({
        title: "Error updating sharing settings",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    loadSafetyPlans();
  }, []);

  return {
    plan,
    allPlans,
    isLoading,
    saveSafetyPlan,
    toggleSharing,
    shareUrl,
    loadPlanById,
    handleNewPlan,
  };
};
